import { useId, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import './Row.css'

const join = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ')

export interface RowLinkProps extends Omit<LinkProps, 'children'> {
  /** Secondary text at the row's right end: a count, a time, a status. */
  meta?: ReactNode
  children: ReactNode
}

/** A row that goes somewhere: the title, optional meta and a chevron, with a rule underneath. */
export function RowLink({ meta, className, children, ...rest }: RowLinkProps) {
  return (
    <Link className={join('row-link', className)} {...rest}>
      <span className="row-link__title">{children}</span>
      {/* The space keeps the title and meta apart in the link's name; flex layout doesn't render it. */}
      {meta ? <> <span className="row-link__meta">{meta}</span></> : null}
      <span className="row-link__chevron" aria-hidden="true" />
    </Link>
  )
}

export interface ChoiceRowProps {
  type: 'radio' | 'checkbox'
  name: string
  value: string
  checked: boolean
  onChange: () => void
  /** An answer's letter ("A"). Without one the row shows a radio dot. */
  letter?: string
  disabled?: boolean
  /** Lines under the label. Screen readers hear them as the choice's description, not its name. */
  description?: ReactNode
  children: ReactNode
}

/**
 * One choice in a list of rules: the real input stays in the DOM for keyboard and screen-reader use but is visually
 * hidden, and the whole row takes the focus ring. A chosen row gets the tint, the orange edge and a filled letter.
 */
export function ChoiceRow({ type, name, value, checked, onChange, letter, disabled, description, children }: ChoiceRowProps) {
  const labelId = useId()
  const descriptionId = useId()
  return (
    <label className={join('choice-row', `choice-row--${type}`, checked && 'choice-row--selected', disabled && 'choice-row--disabled')}>
      <input
        className="visually-hidden"
        type={type}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
      />
      <span className={join('choice-row__mark', letter ? 'choice-row__mark--letter' : 'choice-row__mark--dot')} aria-hidden="true">
        {letter}
      </span>
      <span className="choice-row__body">
        <span id={labelId} className="choice-row__label">
          {children}
        </span>
        {description ? (
          <span id={descriptionId} className="choice-row__description">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  )
}

export interface SectionLabelProps {
  /** A heading when it names the section under it; a plain paragraph otherwise. */
  as?: 'p' | 'h2' | 'h3'
  id?: string
  className?: string
  children: ReactNode
}

/** A small grey label in sentence case ("Your goal", "Try next"), never capitals. */
export function SectionLabel({ as: Tag = 'p', id, className, children }: SectionLabelProps) {
  return (
    <Tag id={id} className={join('section-label', className)}>
      {children}
    </Tag>
  )
}
